import React, { Fragment, useCallback, useRef, useEffect, useState } from 'react';
import { 
  Wand2, 
  Check, 
  ChevronDown, 
  Code, 
  Sparkles, 
  CheckCircle, 
  XCircle, 
  BookOpen,
  User,
  Shield,
  Database,
  Filter,
  Globe,
  Mail,
  Bot,
  DollarSign
} from 'lucide-react';
import { PlaybookTransformItem, PlaybookFilter } from '../../../types/playbook';
import { EventSchema } from '../../../types/schema';
import { FilterTemplate, FilterValidationResult } from '../../../types/ai';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { FILTER_TEMPLATES } from '../../../data/filterTemplates';
import { validateFilterYaml } from '../../../utils/filterValidation';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useUIContext } from '../../../providers/UIProvider';
import YamlCodeViewer from '../YamlCodeViewer';
import axios from 'axios';

interface FilterEditorEnhancedProps {
  transform: PlaybookTransformItem;
  onUpdate: (updates: Partial<PlaybookTransformItem>) => void;
  allEvents?: EventSchema[];
  inputSchema?: any; // For AI context
  outputSchema?: any; // For AI context
  onCloseEditor: () => void;
}

type FilterEditorMode = 'ai' | 'advanced';

interface FilterEditorState {
  mode: FilterEditorMode;
  aiPrompt: string;
  selectedTemplate: FilterTemplate | null; // Change to allow null
  isGenerating: boolean;
  yamlContent: string;
  validationResult?: FilterValidationResult;
  generatedExplanation?: string;
}

// Icon mapping for filter templates
const getTemplateIcon = (templateId: string) => {
  const iconMap: Record<string, React.ComponentType<any>> = {
    'logged-in-users': User,
    'exclude-bots': Bot,
    'event-whitelist': Filter,
    'consent-based': Shield,
    'geographic-filter': Globe,
    'exclude-test-events': Database,
    'minimum-value-filter': DollarSign,
    'email-domain-filter': Mail,
  };
  return iconMap[templateId] || Filter;
};

const FilterEditorEnhanced: React.FC<FilterEditorEnhancedProps> = ({
  transform,
  onUpdate,
  allEvents = [],
  inputSchema,
  onCloseEditor,
}) => {
  const content = transform.content as PlaybookFilter;
  const editorRef = useRef<HTMLDivElement>(null);
  const { showNotification } = useNotifications();
  const { openDocumentationPanel } = useUIContext();

  const [state, setState] = useState<FilterEditorState>({
    mode: 'ai',
    aiPrompt: '',
    selectedTemplate: null, // Initialize to null
    isGenerating: false,
    yamlContent: content.rules?.toString() || '',
    validationResult: undefined,
  });

  // Validate YAML whenever content changes
  useEffect(() => {
    if (state.yamlContent.trim()) {
      const validation = validateFilterYaml(state.yamlContent);
      setState(prev => ({ ...prev, validationResult: validation }));
    } else {
      setState(prev => ({ ...prev, validationResult: undefined }));
    }
  }, [state.yamlContent]);

  const updateContent = (updates: Partial<PlaybookFilter>) => {
    onUpdate({
      content: { ...content, ...updates }
    });
  };

  const updateTransformName = (name: string) => {
    onUpdate({
      name: name
    });
  };

  const handleModeChange = (newMode: FilterEditorMode) => {
    setState(prev => ({ ...prev, mode: newMode }));
  };

  const handleTemplateSelect = (template: FilterTemplate) => {
    setState(prev => ({ 
      ...prev, 
      selectedTemplate: template,
      aiPrompt: `Create a filter using the "${template.name}" template: ${template.description}`
    }));
  };

  const handleOpenDocs = () => {
    openDocumentationPanel('https://docs.metarouter.io/docs/integration-filters');
  };

  const handleGenerateFilter = async () => {
    if (!state.aiPrompt.trim()) return;

    setState(prev => ({ ...prev, isGenerating: true }));

    try {
      // Prepare context for AI
      const inputSchemaProperties = inputSchema ? 
        Object.keys(inputSchema.properties || {}).map(key => ({
          path: key,
          type: inputSchema.properties[key].type || 'string'
        })) : [];

      const availableEvents = allEvents.map(event => event.name);
      const availableProperties = inputSchemaProperties.map(p => p.path);

      const response = await axios.post('/api/ai/generateContent', {
        contentType: 'filterGeneration',
        context: {
          prompt: state.aiPrompt,
          availableEvents,
          availableProperties,
          inputSchemaProperties,
          templates: FILTER_TEMPLATES
        }
      });

      if (response.data.filterGenerationResponse) {
        const { yamlFilter, explanation, suggestedTemplate } = response.data.filterGenerationResponse;
        
        setState(prev => ({ 
          ...prev, 
          yamlContent: yamlFilter,
          generatedExplanation: explanation,
          selectedTemplate: suggestedTemplate ? FILTER_TEMPLATES.find(t => t.id === suggestedTemplate) || null : null
        }));

        // Update the transform content
        updateContent({ rules: yamlFilter });
      }
    } catch (error: any) {
      console.error('Failed to generate filter:', error);
      
      let errorMessage = 'Failed to generate filter. Please try again.';
      
      if (error.response?.status === 503) {
        errorMessage = 'AI service is currently unavailable. Please try again later.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid request. Please check your input and try again.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotification('error', errorMessage, 5000);
    } finally {
      setState(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const handleYamlChange = (newYaml: string) => {
    setState(prev => ({ ...prev, yamlContent: newYaml }));
    updateContent({ rules: newYaml });
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCloseEditor();
    }
  }, [onCloseEditor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editorRef.current && !editorRef.current.contains(event.target as Node)) {
        onCloseEditor();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCloseEditor]);

  return (
    <div ref={editorRef} className="flex flex-col gap-4 py-4 px-4" onKeyDown={handleKeyDown}>

      {/* Filter Name, Quick Templates, and Mode Toggle */}
      <div className="flex items-start gap-4">
        <div className="flex-1 flex flex-col min-h-[66px]">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Filter Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={transform.name || ''}
            onChange={(e) => updateTransformName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white text-sm"
            placeholder="e.g., Adult Users Filter"
          />
        </div>

        {/* Quick Templates */}
        <div className="flex-1 flex flex-col min-h-[66px]">
          <label className="block text-sm font-medium dark:text-gray-300 mb-1">Quick Templates</label>
          <Listbox value={state.selectedTemplate} onChange={handleTemplateSelect}>
            {({ open }) => (
              <div className="relative">
                <ListboxButton className="relative w-full cursor-default rounded border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                  <div className="flex items-center gap-2">
                    {state.selectedTemplate ? (
                      <>
                        {React.createElement(getTemplateIcon(state.selectedTemplate.id), { 
                          className: "h-4 w-4 text-gray-500 dark:text-gray-400" 
                        })}
                        <span className="block truncate">{state.selectedTemplate.name}</span>
                      </>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Select a template...</span>
                    )}
                  </div>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>

                <Transition
                  show={open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-gray-800 dark:border dark:border-gray-700">
                    {FILTER_TEMPLATES.map((template) => {
                      const IconComponent = getTemplateIcon(template.id);
                      return (
                        <ListboxOption
                          key={template.id}
                          className={({ active }) =>
                            clsx(
                              active ? 'text-white bg-blue-600' : 'text-gray-900 dark:text-gray-100',
                              'relative cursor-default select-none py-2 pl-3 pr-9'
                            )
                          }
                          value={template}
                        >
                          {({ selected, active }) => (
                            <>
                              <div className="flex items-center gap-2">
                                <IconComponent className={clsx(
                                  'h-4 w-4',
                                  active ? 'text-white' : 'text-gray-500 dark:text-gray-400'
                                )} />
                                <div className="flex flex-col">
                                  <span className={clsx(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                    {template.name}
                                  </span>
                                  <span className={clsx(
                                    'text-xs truncate',
                                    active ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                                  )}>
                                    {template.description}
                                  </span>
                                </div>
                              </div>
                              {selected ? (
                                <span
                                  className={clsx(
                                    active ? 'text-white' : 'text-blue-600',
                                    'absolute inset-y-0 right-0 flex items-center pr-4'
                                  )}
                                >
                                  <Check className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ListboxOption>
                      );
                    })}
                  </ListboxOptions>
                </Transition>
              </div>
            )}
          </Listbox>
        </div>

        {/* Mode Toggle */}
        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1 justify-center min-h-[32px] mt-5">
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
              state.mode === 'ai' 
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                : 'text-gray-600 dark:text-gray-400'
            }`}
            onClick={() => handleModeChange('ai')}
          >
            <Sparkles className="h-4 w-4" />
            Generate
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
              state.mode === 'advanced' 
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                : 'text-gray-600 dark:text-gray-400'
            }`}
            onClick={() => handleModeChange('advanced')}
          >
            <Code className="h-4 w-4" />
            Code
          </button>
        </div>
      </div>

      {/* AI Mode Interface */}
      {state.mode === 'ai' && (
        <div className="space-y-4">
          {/* AI Prompt Input */}
          <div>
            <label className="block text-sm font-medium dark:text-gray-300 mb-2">Describe your filter</label>
            <div className="relative">
              <textarea
                value={state.aiPrompt}
                onChange={(e) => setState(prev => ({ ...prev, aiPrompt: e.target.value }))}
                placeholder="e.g., 'Only send events from users who have given marketing consent' or 'Filter out bot traffic based on user agent'"
                className="w-full px-3 py-2 pr-20 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white resize-none"
                rows={3}
              />
              <button
                onClick={handleGenerateFilter}
                disabled={!state.aiPrompt.trim() || state.isGenerating}
                className="absolute bottom-3 right-1.5 px-4 py-1 bg-blue-600 text-white rounded text-sm disabled:opacity-50 hover:bg-blue-700 transition-colors flex items-center gap-1"
              >
                {state.isGenerating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Generated Filter Preview */}
          {state.yamlContent && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium dark:text-gray-100">Generated Filter</h4>
                <button
                  onClick={() => handleModeChange('advanced')}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Edit Manually
                </button>
              </div>
              <div className="h-48 border border-gray-200 dark:border-gray-700 rounded">
                <YamlCodeViewer
                  value={state.yamlContent}
                  className="h-full"
                />
              </div>
              {state.generatedExplanation && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <strong>What this filter does:</strong> {state.generatedExplanation}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Advanced Mode Interface */}
      {state.mode === 'advanced' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium dark:text-gray-300 mb-2">YAML Filter Configuration</label>
            <div className="h-80 border border-gray-300 dark:border-gray-600 rounded-md">
              <YamlCodeViewer
                value={state.yamlContent}
                editable={true}
                onChange={handleYamlChange}
                placeholder={`filters:
  - byConditions:
      action: allow
      when:
        inputPathExists:
          inputPath: properties.userId`}
                className="h-full"
              />
            </div>
          </div>
          
          {/* Validation Feedback */}
          {state.validationResult && (
            <div className={`p-3 rounded-md border ${
              state.validationResult.isValid 
                ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200' 
                : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200'
            }`}>
              <div className="flex items-center gap-2">
                {state.validationResult.isValid ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                <span className="font-medium">
                  {state.validationResult.isValid ? 'Valid Filter' : 'Invalid Filter'}
                </span>
              </div>
              {state.validationResult.message && (
                <div className="mt-1 text-sm">{state.validationResult.message}</div>
              )}
              {state.validationResult.explanation && (
                <div className="mt-2 text-sm">
                  <strong>Filter behavior:</strong> {state.validationResult.explanation}
                </div>
              )}
              {state.validationResult.warnings && state.validationResult.warnings.length > 0 && (
                <div className="mt-2">
                  <div className="text-sm font-medium">Warnings:</div>
                  <ul className="text-sm list-disc list-inside">
                    {state.validationResult.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* YAML Reference */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Filter Syntax Reference
            </h5>
            
            <div className="space-y-3 text-sm">
              <div>
                <div className="font-medium text-gray-600 dark:text-gray-400 mb-1">Basic Structure:</div>
                <pre className="text-xs bg-white dark:bg-gray-900 p-2 rounded border dark:border-gray-700 overflow-x-auto dark:text-white">
{`filters:
  - byConditions:
      action: allow|deny
      when:
        [condition]
  - byEventNames:
      action: allow|deny
      events:
        - event1
        - event2`}
                </pre>
              </div>
              
              <div>
                <div className="font-medium text-gray-600 dark:text-gray-400 mb-1">Common Conditions:</div>
                <ul className="text-xs space-y-1 text-gray-600 dark:text-gray-200">
                  <li><code>inputPathExists</code> - Check if a property path exists</li>
                  <li><code>stringEqual</code> - Exact string match</li>
                  <li><code>stringContains</code> - String contains substring</li>
                  <li><code>booleanEqual</code> - Boolean value match</li>
                  <li><code>numericGreaterThan</code> - Numeric comparison</li>
                  <li><code>and</code> / <code>or</code> - Logical operators</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
            {/* Documentation Link */}
      <div className="flex">
        <button
          onClick={handleOpenDocs}
          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
        >
          <BookOpen className="h-4 w-4" />
          Documentation
        </button>
      </div>

    </div>
  );
};

export default FilterEditorEnhanced;
